import type { BeginnerExperience } from "@/lib/strengthStandards"
import type { SubscriptionPlan } from "@/lib/supabase"

/**
 * Textes du diagnostic — validés par Adam sur maquette (lot 3, 28-29/09/2026).
 *
 * Ton de la landing : tutoiement, première personne (c'est Adam qui parle, pas « l'app »).
 * Un changement de mot ici est une décision produit : ne pas reformuler sans lui.
 */

export const DIAGNOSTIC_DURATION = "~2 min"

export const BLOCKS = ["Toi", "Ta pratique", "Ton objectif"] as const

export type LevelId = "beginner" | "intermediate" | "advanced"

export const LEVELS: { id: LevelId; title: string; description: string; bars: number }[] = [
  {
    id: "beginner",
    title: "Débutant",
    description: "Tu débutes ou tu reprends après un arrêt. Tu veux des bases solides et des résultats rapides.",
    bars: 1,
  },
  {
    id: "intermediate",
    title: "Intermédiaire",
    description: "Tu t'entraînes régulièrement depuis 1 à 3 ans. Tu veux franchir un cap.",
    bars: 2,
  },
  {
    id: "advanced",
    title: "Avancé",
    description: "Plus de 3 ans d'entraînement intensif. Tu vises la performance, séance après séance.",
    bars: 3,
  },
]

/**
 * Questionnaire d'expérience Débutant — libellés et valeurs INCHANGÉS (décision du lot 3),
 * repris mot pour mot de l'ancien `StepLevel`. Changer un seuil ou le sens d'une option
 * changerait silencieusement le sens des valeurs déjà stockées (audit §5.2).
 */
export const BEGINNER_EXPERIENCE_OPTIONS: { id: BeginnerExperience; label: string }[] = [
  { id: "vrai_debutant", label: "Je débute, je n'ai jamais fait de musculation" },
  { id: "reprise_recente", label: "J'ai déjà pratiqué, arrêté il y a moins de 2 ans" },
  { id: "reprise_ancienne", label: "J'ai déjà pratiqué, arrêté il y a plus de 2 ans" },
]

/** Titres des cartes objectif — ceux de l'ancien `StepGoal`. La liste dépend du niveau. */
export const GOALS_BY_LEVEL: Record<LevelId, { id: string; title: string }[]> = {
  beginner: [
    { id: "perte-de-poids", title: "Perte de poids" },
    { id: "prise-de-muscle", title: "Prise de muscle" },
    { id: "remise-en-forme", title: "Remise en forme & Tonification" },
  ],
  intermediate: [
    { id: "seche-definition", title: "Sèche & Définition" },
    { id: "construction-musculaire", title: "Construction Musculaire" },
    { id: "force-endurance", title: "Force & Endurance" },
  ],
  advanced: [
    { id: "decoupe-extreme", title: "Découpe Extrême" },
    { id: "hypertrophie", title: "Hypertrophie" },
    { id: "force-athletique", title: "Force Athlétique" },
  ],
}

export const PLACES: { id: "gym" | "home"; title: string; short: string }[] = [
  { id: "gym", title: "Salle de sport", short: "en salle" },
  { id: "home", title: "À la maison", short: "à la maison" },
]

/** Il n'existe pas de programme Avancé à la maison : le catalogue ne l'a pas. */
export const ADVANCED_HOME_NOTE = "Le programme Avancé se fait en salle."

/** Molettes de l'écran « Ton point de départ » — bornes et valeurs de départ de l'ancien wizard. */
export const METRICS = [
  { key: "age", label: "Âge", unit: "ans", min: 16, max: 90, initial: 25 },
  { key: "height", label: "Taille", unit: "cm", min: 140, max: 220, initial: 175 },
  { key: "weight", label: "Poids", unit: "kg", min: 40, max: 200, initial: 75 },
] as const

// ── Consentement « blessures » (RGPD art. 9) : partagé avec Mon programme ──
export { INJURY_CONSENT_VERSION, INJURY_CONSENT_LABEL, INJURY_CONSENT_DETAILS } from "@/data/injuryConsent"

// ── Recommandation en cas de douleur ────────────────────────────────────────
export const PAIN_RECOMMENDATION = {
  eyebrow: "À propos de ta blessure",
  lead: "Une blessure récente ou encore douloureuse, je préfère la suivre de près.",
  body: "Revenir de blessure, c'est ma spécialité : mon Master porte sur la réathlétisation. Avec Coaching ou Premium, on commence par un bilan de départ ensemble, et je construis une reprise progressive autour de ta blessure.",
  reassurance: "Tu peux quand même démarrer ta semaine d'essai. Si un exercice réveille la douleur, arrête-le. Zeyone ne remplace pas un avis médical.",
  cta: "Réserver mon appel découverte",
  mailSubject: "Appel découverte — reprise après blessure",
  mailBody: "Bonjour Adam,\n\nJe viens de faire mon diagnostic et j'ai une blessure qui me gêne encore. J'aimerais en parler avec toi.\n\nMes disponibilités : ",
}

// ── Textes qui dépendent de la formule ──────────────────────────────────────
//
// Aux lots 6 et 7, Coaching et Premium font le diagnostic APRÈS le paiement : leur
// écran de fin mène au choix de la date du bilan, pas à un essai. Il suffira
// d'ajouter leur ligne ici — aucune condition « si Coaching » dans les écrans.
export type ParcoursTexts = {
  finCta: string
  finCtaBusy: string
  finFoot: string
}

export const PARCOURS_TEXTS: Partial<Record<SubscriptionPlan, ParcoursTexts>> = {
  autonomie: {
    finCta: "Démarrer ma semaine d'essai",
    finCtaBusy: "Je prépare ton calendrier…",
    finFoot: "7 jours d'essai gratuit · accès complet · sans engagement",
  },
}

export const TRIAL_ALREADY_USED =
  "Cette adresse a déjà eu sa semaine d'essai. Ton diagnostic est enregistré : choisis ton accompagnement pour démarrer."
